import { FC, useCallback, LegacyRef } from 'react';
import { Flex, Checkbox } from '@highereducation/hen-ui';
import type { QuestionProps } from '@de/components/Step/Step';
import { parseHtmlString } from '@de/shared/utils';
import { useDynamicExperienceContext } from '@de/providers/DynamicExperienceProvider';
import useQuestionEvents from '@de/hooks/useQuestionEvents';
import { OptionsLoadingPlaceholder } from '@de/components/Step/OptionsLoadingPlaceholder';

/**
 * CheckboxQuestion component using hen-ui design system.
 *
 * A checkbox-style question component built with hen-ui atomic components,
 * following EDU Design System standards. Displays selectable options with
 * visual checkbox feedback.
 *
 * @example
 * <CheckboxQuestion
 *   id="terms"
 *   options={[{ optionId: '1', text: 'I agree to terms' }]}
 *   leadFieldAssociated="agreedToTerms"
 *   stepName="Terms"
 * />
 */
const CheckboxQuestion: FC<QuestionProps> = ({
  options = [],
  id = '',
  leadFieldAssociated,
  automaticNext = false,
  isMultiQuestionChild = false,
  title,
  stepName,
  variant,
  isOptionsLoading = false,
}) => {
  const { updateFlowState, getStepMetadataById, handleStepForward } =
    useDynamicExperienceContext();
  const stepSelectedValue = getStepMetadataById(id || '');

  const { ref: visibilityRef, triggerFieldInputted } = useQuestionEvents({
    id,
    fieldType: 'checkbox',
    options,
    title,
    stepName,
    isMultiQuestionChild,
    variant,
    leadFieldAssociated,
  });

  const handleOptionClick = useCallback(
    (
      option: NonNullable<QuestionProps['options']>[number],
      selectedAfter: boolean,
    ) => {
      if (!selectedAfter) {
        updateFlowState(id, {});
      } else {
        updateFlowState(id, { option, leadFieldAssociated });
      }
      if (automaticNext && !isMultiQuestionChild) {
        handleStepForward();
      }
      triggerFieldInputted(option.text as string);
    },
    [
      automaticNext,
      handleStepForward,
      id,
      isMultiQuestionChild,
      leadFieldAssociated,
      triggerFieldInputted,
      updateFlowState,
    ],
  );

  return (
    <Flex
      className='mt-md'
      justify='center'
      ref={visibilityRef as LegacyRef<HTMLDivElement>}
      gap='xxs'
    >
      {isOptionsLoading ? (
        <OptionsLoadingPlaceholder />
      ) : (
        options.map((option) => {
          const isSelected =
            (stepSelectedValue.option &&
              !Array.isArray(stepSelectedValue.option) &&
              stepSelectedValue.option.optionId === option.optionId) ??
            false;
          const checkboxId = `${id}-${option.optionId}`;

          return (
            <Flex key={option.optionId}>
              <Checkbox
                id={checkboxId}
                label={parseHtmlString(option.text)}
                checked={isSelected}
                onCheckedChange={(checked) =>
                  handleOptionClick(option, Boolean(checked))
                }
              />
            </Flex>
          );
        })
      )}
    </Flex>
  );
};

export default CheckboxQuestion;
